import React, { useEffect, useMemo, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, ModalityBadge, catColor, modColor } from "@/components/Shared";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { format, startOfWeek, addDays, addWeeks, subWeeks, startOfMonth, endOfMonth, endOfWeek, addMonths, subMonths, isSameMonth, isSameDay, parseISO } from "date-fns";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, Plus, Trash2, AlertTriangle, GripVertical, UserPlus, Wand2 } from "lucide-react";

const fmt = (d) => format(d, "yyyy-MM-dd");
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function ScheduleCalendar() {
  const [view, setView] = useState("week");
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [monthCursor, setMonthCursor] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(fmt(new Date()));
  const [shifts, setShifts] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editShift, setEditShift] = useState(null);
  const [genOpen, setGenOpen] = useState(false);

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const monthDays = useMemo(() => {
    const first = startOfWeek(startOfMonth(monthCursor), { weekStartsOn: 1 });
    const last = endOfWeek(endOfMonth(monthCursor), { weekStartsOn: 1 });
    const arr = [];
    for (let d = first; d <= last; d = addDays(d, 1)) arr.push(d);
    return arr;
  }, [monthCursor]);

  const range = useMemo(
    () => (view === "week" ? [days[0], days[6]] : [monthDays[0], monthDays[monthDays.length - 1]]),
    [view, days, monthDays],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sh, st] = await Promise.all([
        api.get(`/shifts?start=${fmt(range[0])}&end=${fmt(range[1])}`),
        api.get("/staff"),
      ]);
      setShifts(sh.data);
      setStaff(st.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => { load(); }, [load]);

  const shiftsByDay = useMemo(() => {
    const map = {};
    shifts.forEach((s) => { (map[s.date] = map[s.date] || []).push(s); });
    Object.values(map).forEach((l) => l.sort((a, b) => a.start_time.localeCompare(b.start_time)));
    return map;
  }, [shifts]);

  const onDragEnd = async (result) => {
    const { destination, draggableId } = result;
    if (!destination) return;
    if (destination.droppableId.startsWith("shift-")) {
      const shiftId = destination.droppableId.replace("shift-", "");
      const techId = draggableId.replace("tech-", "");
      try {
        const res = await api.patch(`/shifts/${shiftId}/assign`, { tech_id: techId });
        setShifts((prev) => prev.map((s) => (s.id === shiftId ? res.data : s)));
        if (res.data.conflicts && res.data.conflicts.length) {
          toast.warning("Assigned with conflict", { description: res.data.conflicts[0] });
        } else {
          toast.success("Tech assigned");
        }
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      }
    }
  };

  const unassign = async (shiftId) => {
    try {
      const res = await api.patch(`/shifts/${shiftId}/assign`, { tech_id: null });
      setShifts((prev) => prev.map((s) => (s.id === shiftId ? res.data : s)));
      toast.success("Shift cleared");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const removeShift = async (shiftId) => {
    try {
      await api.delete(`/shifts/${shiftId}`);
      setShifts((prev) => prev.filter((s) => s.id !== shiftId));
      toast.success("Shift deleted");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openNew = (day) => { setEditShift({ date: typeof day === "string" ? day : fmt(day) }); setModalOpen(true); };

  const rangeLabel = view === "week"
    ? `${format(days[0], "MMM d")} – ${format(days[6], "MMM d, yyyy")}`
    : format(monthCursor, "MMMM yyyy");
  const stepBack = () => (view === "week" ? setWeekStart(subWeeks(weekStart, 1)) : setMonthCursor(subMonths(monthCursor, 1)));
  const stepFwd = () => (view === "week" ? setWeekStart(addWeeks(weekStart, 1)) : setMonthCursor(addMonths(monthCursor, 1)));

  return (
    <div>
      <PageHeader title="Schedule" subtitle={view === "week" ? "// Drag a tech onto a shift to assign" : "// Tap a day to view and assign shifts"}>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex border border-slate-800 mr-1">
            {[["week", "Week"], ["month", "Month"]].map(([v, label]) => (
              <button key={v} data-testid={`view-${v}-btn`} onClick={() => setView(v)}
                className={`px-3 py-2 text-xs font-mono-rs uppercase tracking-wider transition-all ${
                  view === v ? "bg-[#D902EE]/20 text-white border-b-2 border-[#D902EE]" : "text-slate-400 hover:text-white"}`}>
                {label}
              </button>
            ))}
          </div>
          <button data-testid="prev-week-btn" onClick={stepBack}
            className="p-2 border border-slate-700 text-slate-300 hover:border-[#00F0FF] hover:text-[#00F0FF] transition-all duration-300">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="font-mono-rs text-sm text-slate-300 min-w-[160px] text-center">{rangeLabel}</span>
          <button data-testid="next-week-btn" onClick={stepFwd}
            className="p-2 border border-slate-700 text-slate-300 hover:border-[#00F0FF] hover:text-[#00F0FF] transition-all duration-300">
            <ChevronRight className="w-4 h-4" />
          </button>
          <button data-testid="generate-schedule-btn" onClick={() => setGenOpen(true)}
            className="flex items-center gap-2 px-4 py-2 border border-[#00F0FF] bg-[#00F0FF]/10 text-white text-sm font-mono-rs uppercase tracking-wider hover:bg-[#00F0FF]/25 hover:rs-glow-cyan transition-all duration-300">
            <Wand2 className="w-4 h-4" /> Auto-Generate
          </button>
          <button data-testid="add-shift-btn" onClick={() => openNew(view === "week" ? days[0] : selectedDay)}
            className="flex items-center gap-2 px-4 py-2 border border-[#D902EE] bg-[#D902EE]/10 text-white text-sm font-mono-rs uppercase tracking-wider hover:bg-[#D902EE]/25 transition-all duration-300">
            <Plus className="w-4 h-4" /> Shift
          </button>
        </div>
      </PageHeader>

      {view === "week" ? (
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="grid lg:grid-cols-[240px_1fr] gap-4">
            <div className="bg-[#0F1219] border border-slate-800 p-4 h-fit lg:sticky lg:top-4" data-testid="tech-roster">
              <h3 className="font-mono-rs text-xs tracking-[0.2em] uppercase text-slate-400 mb-3">Tech Roster</h3>
              <Droppable droppableId="roster" isDropDisabled={true}>
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-2">
                    {staff.map((t, i) => (
                      <Draggable key={t.id} draggableId={`tech-${t.id}`} index={i}>
                        {(prov, snap) => (
                          <div ref={prov.innerRef} {...prov.draggableProps} {...prov.dragHandleProps}
                            data-testid={`roster-tech-${t.id}`}
                            className={`flex items-center gap-2 px-3 py-2 bg-[#05060A] border-l-2 cursor-grab active:cursor-grabbing transition-all duration-200 ${
                              snap.isDragging ? "rs-glow-magenta scale-[1.03]" : "hover:bg-[#171A21]"
                            }`}
                            style={{ borderLeftColor: catColor(t.category) }}>
                            <GripVertical className="w-3 h-3 text-slate-600" />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm text-white truncate">{t.name}</div>
                              <div className="text-[10px] font-mono-rs uppercase tracking-wider"
                                style={{ color: catColor(t.category) }}>{t.category === "Both" ? "CT+XR" : t.category}</div>
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                    {staff.length === 0 && <p className="text-xs text-slate-500">No techs yet.</p>}
                  </div>
                )}
              </Droppable>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-7 gap-3">
              {days.map((day) => {
                const key = fmt(day);
                const isToday = key === fmt(new Date());
                return (
                  <div key={key} className={`bg-[#0F1219] border ${isToday ? "border-[#00F0FF]/50" : "border-slate-800"} min-h-[180px] flex flex-col`}>
                    <div className={`px-3 py-2 border-b border-slate-800 flex items-center justify-between ${isToday ? "bg-[#00F0FF]/5" : ""}`}>
                      <div>
                        <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500">{format(day, "EEE")}</div>
                        <div className="font-mono-rs text-lg text-white leading-none">{format(day, "d")}</div>
                      </div>
                      <button data-testid={`add-shift-day-${key}`} onClick={() => openNew(day)}
                        className="text-slate-500 hover:text-[#D902EE] transition-colors"><Plus className="w-4 h-4" /></button>
                    </div>
                    <div className="p-2 space-y-2 flex-1">
                      {(shiftsByDay[key] || []).map((s) => (
                        <Droppable key={s.id} droppableId={`shift-${s.id}`}>
                          {(provided, snapshot) => (
                            <div ref={provided.innerRef} {...provided.droppableProps}
                              data-testid={`shift-card-${s.id}`}
                              className={`group border-l-2 bg-[#05060A] p-2 transition-all duration-200 ${
                                snapshot.isDraggingOver ? "rs-glow-cyan bg-[#171A21]" : ""
                              } ${s.conflicts?.length ? "ring-1 ring-[#FF003C]/50" : ""}`}
                              style={{ borderLeftColor: modColor(s.modality) }}>
                              <div className="flex items-center justify-between mb-1">
                                <ModalityBadge modality={s.modality} />
                                <div className="flex items-center gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                                  <button data-testid={`edit-shift-${s.id}`} onClick={() => { setEditShift(s); setModalOpen(true); }}
                                    className="text-slate-500 hover:text-[#00F0FF]"><UserPlus className="w-3.5 h-3.5" /></button>
                                  <button data-testid={`delete-shift-${s.id}`} onClick={() => removeShift(s.id)}
                                    className="text-slate-500 hover:text-[#FF003C]"><Trash2 className="w-3.5 h-3.5" /></button>
                                </div>
                              </div>
                              <div className="font-mono-rs text-xs text-white mb-1">{s.start_time}–{s.end_time}</div>
                              {s.tech_name ? (
                                <div className="flex items-center justify-between">
                                  <span className="text-xs text-slate-200 truncate">{s.tech_name}</span>
                                  <button data-testid={`unassign-${s.id}`} onClick={() => unassign(s.id)}
                                    className="text-[9px] text-slate-600 hover:text-[#FF003C] uppercase">clear</button>
                                </div>
                              ) : (
                                <div className="text-[10px] font-mono-rs uppercase tracking-wider text-[#FFB800] border border-dashed border-[#FFB800]/40 px-1 py-0.5 text-center">
                                  Drop tech here
                                </div>
                              )}
                              {s.conflicts?.length > 0 && (
                                <div className="mt-1 flex items-start gap-1 text-[10px] text-[#FF003C]">
                                  <AlertTriangle className="w-3 h-3 mt-px shrink-0" />
                                  <span>{s.conflicts[0]}</span>
                                </div>
                              )}
                              <div className="hidden">{provided.placeholder}</div>
                            </div>
                          )}
                        </Droppable>
                      ))}
                      {(shiftsByDay[key] || []).length === 0 && (
                        <div className="text-[10px] text-slate-600 text-center py-4">No shifts</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </DragDropContext>
      ) : (
        <div className="grid lg:grid-cols-[1fr_320px] gap-4">
          {/* Month grid */}
          <div>
            <div className="grid grid-cols-7 gap-2 mb-2">
              {WEEKDAYS.map((d) => (
                <div key={d} className="text-center text-[10px] tracking-[0.2em] uppercase text-slate-500 font-mono-rs py-1">{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-2" data-testid="admin-month-grid">
              {monthDays.map((d) => {
                const key = fmt(d);
                const dayShifts = shiftsByDay[key] || [];
                const inMonth = isSameMonth(d, monthCursor);
                const isToday = isSameDay(d, new Date());
                const isSel = key === selectedDay;
                const unstaffed = dayShifts.filter((s) => !s.tech_id).length;
                return (
                  <button key={key} data-testid={`month-day-${key}`} onClick={() => setSelectedDay(key)}
                    className={`min-h-[96px] p-2 text-left border transition-all duration-200 ${
                      isSel ? "border-[#D902EE] bg-[#D902EE]/10 rs-glow-magenta"
                      : dayShifts.length ? "border-slate-700 bg-[#0F1219] hover:border-[#00F0FF]/50"
                      : "border-slate-800/60 bg-[#0B0D13]"} ${inMonth ? "" : "opacity-40"}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className={`font-mono-rs text-sm ${isToday ? "text-[#00F0FF]" : "text-white"}`}>{format(d, "d")}</span>
                      {unstaffed > 0 && <span className="text-[9px] font-mono-rs text-[#FFB800]">{unstaffed}!</span>}
                    </div>
                    {dayShifts.slice(0, 3).map((s) => (
                      <div key={s.id} className="mb-1 px-1 py-0.5 border-l-2 text-[9px] font-mono-rs text-slate-200 truncate flex items-center justify-between"
                        style={{ borderLeftColor: modColor(s.modality), background: modColor(s.modality) + "1A" }}>
                        <span>{s.start_time}</span>
                        <span className="text-slate-400 truncate ml-1">{s.tech_name ? s.tech_name.split(" ").slice(-1)[0] : "—"}</span>
                      </div>
                    ))}
                    {dayShifts.length > 3 && <div className="text-[9px] text-slate-500">+{dayShifts.length - 3} more</div>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Selected day panel */}
          <div className="bg-[#0F1219] border border-slate-800 p-4 h-fit lg:sticky lg:top-4" data-testid="month-day-panel">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-[#00F0FF] font-mono-rs mb-1">Selected day</div>
                <div className="font-mono-rs text-white">{format(parseISO(selectedDay), "EEE, MMM d")}</div>
              </div>
              <button data-testid="month-add-shift" onClick={() => openNew(selectedDay)}
                className="flex items-center gap-1 px-3 py-1.5 border border-[#D902EE] text-[11px] font-mono-rs uppercase text-white hover:bg-[#D902EE]/20 transition-all">
                <Plus className="w-3.5 h-3.5" /> Shift
              </button>
            </div>
            <div className="space-y-2">
              {(shiftsByDay[selectedDay] || []).length === 0 && (
                <p className="text-xs text-slate-500">No shifts this day.</p>
              )}
              {(shiftsByDay[selectedDay] || []).map((s) => (
                <div key={s.id} data-testid={`month-shift-${s.id}`}
                  className={`border-l-2 bg-[#05060A] p-2 ${s.conflicts?.length ? "ring-1 ring-[#FF003C]/50" : ""}`}
                  style={{ borderLeftColor: modColor(s.modality) }}>
                  <div className="flex items-center justify-between mb-1">
                    <ModalityBadge modality={s.modality} />
                    <div className="flex items-center gap-1">
                      <button data-testid={`edit-shift-${s.id}`} onClick={() => { setEditShift(s); setModalOpen(true); }}
                        className="text-slate-500 hover:text-[#00F0FF]"><UserPlus className="w-3.5 h-3.5" /></button>
                      <button data-testid={`delete-shift-${s.id}`} onClick={() => removeShift(s.id)}
                        className="text-slate-500 hover:text-[#FF003C]"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                  <div className="font-mono-rs text-xs text-white mb-1">{s.start_time}–{s.end_time}</div>
                  {s.tech_name ? (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-200 truncate">{s.tech_name}</span>
                      <button data-testid={`unassign-${s.id}`} onClick={() => unassign(s.id)}
                        className="text-[9px] text-slate-600 hover:text-[#FF003C] uppercase">clear</button>
                    </div>
                  ) : (
                    <div className="text-[10px] font-mono-rs uppercase tracking-wider text-[#FFB800] border border-dashed border-[#FFB800]/40 px-1 py-0.5 text-center">
                      Unstaffed — tap edit to assign
                    </div>
                  )}
                  {s.conflicts?.length > 0 && (
                    <div className="mt-1 flex items-start gap-1 text-[10px] text-[#FF003C]">
                      <AlertTriangle className="w-3 h-3 mt-px shrink-0" /><span>{s.conflicts[0]}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {loading && <div className="text-center text-[#00F0FF] font-mono-rs text-xs tracking-[0.3em] py-6">LOADING…</div>}

      <ShiftModal open={modalOpen} onOpenChange={setModalOpen} shift={editShift} staff={staff} onSaved={load} />
      <GenerateModal open={genOpen} onOpenChange={setGenOpen} onDone={load} />
    </div>
  );
}

function GenerateModal({ open, onOpenChange, onDone }) {
  const [startDate, setStartDate] = useState(fmt(new Date()));
  const [months, setMonths] = useState(12);
  const [replace, setReplace] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setStartDate(fmt(new Date())); setMonths(12); setReplace(true); } }, [open]);

  const inputCls = "w-full bg-[#05060A] border border-slate-700 px-3 py-2 text-sm text-white outline-none focus:border-[#00F0FF] transition-colors";

  const run = async () => {
    setBusy(true);
    try {
      const res = await api.post("/schedule/generate", { start_date: startDate, months: Number(months), replace });
      toast.success("Schedule generated", { description: `${res.data.created} shifts created · ${res.data.assigned} auto-assigned` });
      onOpenChange(false);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0F1219] border border-[#00F0FF]/40 text-white rounded-none max-w-md" data-testid="generate-modal">
        <DialogHeader>
          <DialogTitle className="font-mono-rs uppercase tracking-wider flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-[#00F0FF]" /> Auto-Generate Schedule
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <p className="text-xs text-slate-400 leading-relaxed">
            Applies a <span className="text-[#00F0FF]">Panama 2-2-3 rotation</span> on <span className="text-[#00F0FF]">12-hour shifts</span>
            (Day 07:00–19:00, Night 19:00–07:00, 24/7). Full-time techs are guaranteed <span className="text-[#34E5B0]">≥38h every week</span>
            (topped up as needed). Days staff 2 techs (CT + X-ray); nights 1 dual-cert tech. Honors shift preferences &amp; approved
            time-off (the only exemption from the weekly minimum). Edit anything afterward.
          </p>
          <div>
            <label className="text-xs tracking-[0.2em] uppercase text-slate-400 mb-1 block">Start Date</label>
            <input data-testid="gen-start-input" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="text-xs tracking-[0.2em] uppercase text-slate-400 mb-1 block">Duration</label>
            <div className="flex gap-2">
              {[3, 6, 12].map((m) => (
                <button key={m} data-testid={`gen-months-${m}`} onClick={() => setMonths(m)}
                  className={`flex-1 py-2 text-xs font-mono-rs uppercase border transition-all ${
                    months === m ? "border-[#00F0FF] bg-[#00F0FF]/15 text-white" : "border-slate-700 text-slate-400"}`}>
                  {m} mo
                </button>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-3 cursor-pointer">
            <input data-testid="gen-replace-toggle" type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)}
              className="w-4 h-4 accent-[#D902EE]" />
            <span className="text-sm text-slate-300">Replace existing shifts in this range</span>
          </label>
        </div>
        <DialogFooter>
          <button data-testid="gen-run-btn" onClick={run} disabled={busy}
            className="w-full py-2 font-mono-rs uppercase tracking-wider text-sm border border-[#00F0FF] bg-[#00F0FF]/10 hover:bg-[#00F0FF]/25 transition-all duration-300 disabled:opacity-50">
            {busy ? "Generating…" : "Generate Schedule"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ShiftModal({ open, onOpenChange, shift, staff, onSaved }) {
  const [form, setForm] = useState({});
  useEffect(() => {
    setForm({
      date: shift?.date || fmt(new Date()),
      start_time: shift?.start_time || "07:00",
      end_time: shift?.end_time || "15:00",
      modality: shift?.modality || "CT",
      tech_id: shift?.tech_id || "",
    });
  }, [shift, open]);

  const isEdit = shift && shift.id;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    const payload = { ...form, tech_id: form.tech_id || null };
    try {
      if (isEdit) {
        const res = await api.put(`/shifts/${shift.id}`, payload);
        if (res.data.conflicts?.length) toast.warning("Saved with conflict", { description: res.data.conflicts[0] });
        else toast.success("Shift updated");
      } else {
        const res = await api.post("/shifts", payload);
        if (res.data.conflicts?.length) toast.warning("Created with conflict", { description: res.data.conflicts[0] });
        else toast.success("Shift created");
      }
      onOpenChange(false);
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const inputCls = "w-full bg-[#05060A] border border-slate-700 px-3 py-2 text-sm text-white outline-none focus:border-[#00F0FF] transition-colors";
  const eligible = staff.filter((t) =>
    form.modality === "CT/X-ray" ? t.category === "Both" : (t.category === form.modality || t.category === "Both"));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0F1219] border border-[#D902EE]/40 text-white rounded-none max-w-md" data-testid="shift-modal">
        <DialogHeader>
          <DialogTitle className="font-mono-rs uppercase tracking-wider">{isEdit ? "Edit Shift" : "New Shift"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label className="text-xs tracking-[0.2em] uppercase text-slate-400 mb-1 block">Date</label>
            <input data-testid="shift-date-input" type="date" value={form.date || ""} onChange={(e) => set("date", e.target.value)} className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-slate-400 mb-1 block">Start</label>
              <input data-testid="shift-start-input" type="time" value={form.start_time || ""} onChange={(e) => set("start_time", e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-slate-400 mb-1 block">End</label>
              <input data-testid="shift-end-input" type="time" value={form.end_time || ""} onChange={(e) => set("end_time", e.target.value)} className={inputCls} />
            </div>
          </div>
          <div>
            <label className="text-xs tracking-[0.2em] uppercase text-slate-400 mb-1 block">Modality</label>
            <div className="grid grid-cols-3 gap-2">
              {[["CT", "CT"], ["X-ray", "X-ray"], ["CT/X-ray", "CT/XR"]].map(([m, label]) => (
                <button key={m} data-testid={`modality-${m}`} onClick={() => { set("modality", m); set("tech_id", ""); }}
                  className={`py-2 text-[11px] font-mono-rs uppercase border transition-all ${
                    form.modality === m
                      ? (m === "CT" ? "border-[#D902EE] bg-[#D902EE]/15 text-white"
                         : m === "X-ray" ? "border-[#00F0FF] bg-[#00F0FF]/15 text-white"
                         : "border-[#34E5B0] bg-[#34E5B0]/15 text-white")
                      : "border-slate-700 text-slate-400"}`}>{label}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs tracking-[0.2em] uppercase text-slate-400 mb-1 block">Assign Tech ({form.modality})</label>
            <select data-testid="shift-tech-select" value={form.tech_id || ""} onChange={(e) => set("tech_id", e.target.value)} className={inputCls}>
              <option value="">— Unassigned —</option>
              {eligible.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        </div>
        <DialogFooter>
          <button data-testid="shift-save-btn" onClick={save}
            className="w-full py-2 font-mono-rs uppercase tracking-wider text-sm border border-[#D902EE] bg-[#D902EE]/10 hover:bg-[#D902EE]/25 transition-all duration-300">
            {isEdit ? "Save Changes" : "Create Shift"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
